export function buildAnalyticsFileData(settings, identitySyncs, previousIdentitySyncs) {
  const analyticsFileSettings = {
    host: settings.host,
    cdnURL: settings.cdnURL,
    compressFile: settings.compressFile,
    minifyFile: settings.minifyFile,
    impliedConsent: settings.syncInjectorSettings.privacy.impliedConsent,
    cookieName: settings.syncInjectorSettings.privacy.cookieName,
    itpSettings: settings.syncInjectorSettings.itpSettings.enabled,
    fileHostingType: settings.fileHostingType,
    windowVariableName: settings.windowVariableName,
    /**
     * Currently we only use one bot detection library, but we may add more in the future.
     * If settings are passed, we will enable it, otherwise we will set this as false
     */
    botFilterEnabled: settings.syncInjectorSettings?.botDetectionSettings !== undefined,
    /**
     * We store the code of the ID in the database, this is passed from
     * packages/app/src/components/analytics-builder/create-ajs-builder-file.js
     * To the builder server we pass the actual code.
     */
    matchIDConsentCode: settings.syncInjectorSettings?.consent?.matchIDConsentCodeID
      ? settings.syncInjectorSettings.consent.matchIDConsentCodeID
      : null,
    /**
     * The builder-server ConsentSettings shape is stored verbatim (jsonb) so a
     * stored config can be rebuilt with full fidelity. NULL = legacy row whose
     * consent source derives from cookie_name.
     */
    consentSource: settings.syncInjectorSettings?.consent?.consentSource ?? null,
    optInValues: settings.syncInjectorSettings?.consent?.optInValues ?? null,
    globalPrivacyControl: settings.syncInjectorSettings?.consent?.globalPrivacyControl ?? null,
    /**
     * We use settings.syncInjectorSettings.storage.marker because that is the location of those settings in
     * the builder server, see file packages/app/src/components/analytics-builder/create-ajs-builder-file.js, that
     * is the actual request that goes to the builder server.
     */
    storagePrefix: settings.syncInjectorSettings.storage.marker,
    storageType: settings.syncInjectorSettings.storage.type,
    storageDatabase: settings.syncInjectorSettings.storage.database,
  };

  const analyticsFileIdentitySyncs = identitySyncs.map(identitySync => {
    return {
      settings: identitySync.settings,
      settingsVersion: getSettingsVersion(identitySync, previousIdentitySyncs),
      enableResyncOnSettingsChange: identitySync.enableResyncOnSettingsChange === true,
      consentCategoryId: identitySync.consentCategoryId ?? null,
      tcfEnabled: identitySync.tcfEnabled ?? false,
      identity_sync: {
        on_conflict: {
          constraint: 'identity_syncs_name_key',
          update_columns: ['friendlyName'],
        },
        data: {
          name: identitySync.name,
          friendlyName: identitySync.friendlyName,
        },
      },
    };
  });

  // JSON.stringify doesn't guarantee key order, so identical objects with different
  // key ordering would produce different strings and cause false version bumps.
  // This recursively sorts keys at every level before stringifying to ensure
  // consistent output for deep comparison (e.g. Google Gtag consentModeSettings,
  // Adobe syncFire, etc.).
  function stableStringify(value) {
    if (value === null || typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) return '[' + value.map(stableStringify).join(',') + ']';
    return (
      '{' +
      Object.keys(value)
        .sort()
        .map(k => JSON.stringify(k) + ':' + stableStringify(value[k]))
        .join(',') +
      '}'
    );
  }

  /**
   * Returns true if settings (deep), tcfEnabled, or consentCategoryId changes.
   * @param {{ settings?: any, tcfEnabled?: boolean, consentCategoryId?: string|null }} current
   * @param {{ settings?: any, tcfEnabled?: boolean, consentCategoryId?: string|null }} previous
   * @returns {boolean}
   */
  function hasMeaningfulSettingsChange(current, previous) {
    const currSettings = current.settings ?? {};
    const prevSettings = previous.settings ?? {};

    return (
      stableStringify(prevSettings) !== stableStringify(currSettings) ||
      (previous.consentCategoryId ?? null) !== (current.consentCategoryId ?? null) ||
      Boolean(previous.tcfEnabled) !== Boolean(current.tcfEnabled)
    );
  }

  /**
   * @returns {number}
   */
  function getSettingsVersion(identitySync, previousIdentitySyncs) {
    const previous = previousIdentitySyncs?.find(s => s.name === identitySync.name);

    // No previous record, default to version 1
    if (!previous) return 1;

    const baseVersion = previous.settingsVersion ?? 1;

    return hasMeaningfulSettingsChange(identitySync, previous) ? baseVersion + 1 : baseVersion;
  }

  return {
    analyticsFileSettings,
    analyticsFileIdentitySyncs,
  };
}
